import emojiRegex from 'emoji-regex';

/**
 *
 * @summary form-validations
 * @version 1.0.0
 * @author Alex Dinari <alex.dinari@aofl.com>
 */

const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const CHECKABLE_DOMAINS = {
  gmail: 6,
  hotmail: 4,
  live: 4,
  msn: 4,
  yahoo: 4,
  ymail: 4
};

/**
 * apiInputErrorValidator
 *
 * @param {Number} key
 * @param {String} type
 * @param {String} message
 * @return {Function}
 */
const apiInputErrorValidator = (key, type, message) => {
  return (value) => {
    if (value == null || typeof value?.error?.input_errors === 'undefined') return true;
    const inputErorrs = value.error.input_errors;

    if (message === 'required_fields_absent') {
      return inputErorrs?.required_fields_absent && inputErorrs.required_fields_absent.indexOf(key);
    }

    return !(inputErrors?.[key]?.[type] === message);
  };
};


/**
 * apiErrorValidator
 *
 * @param {Array} errorCodes
 * @return {Function}
 */
const apiErrorValidator = (errorCodes) => {
  if (!Array.isArray(errorCodes)) {
    throw new TypeError(`errorCodes must be an array.`);
  } else if (errorCodes.length === 0) {
    throw new Error(`errorCodes cannot be an empty array.`);
  }

  return (value) => {
    return errorCodes.indexOf(value?.error?.code) === -1;
  };
};


/**
 * uncaughtApiErrorsValidator
 *
 * @param {Array} ignoreList
 * @return {Function}
 */

const uncaughtApiErrorsValidator = (ignoreList = []) => async function(value) {
  const objectPathArray = this.propName.split('.'); // eslint-disable-line
  let validationObject = this.target.form; // eslint-disable-line
  for (let i = 0; i < objectPathArray.length - 1; i++) {
    validationObject = validationObject[objectPathArray[i]];
  }

  let valid = true;
  for (const key in validationObject) {
    if (!validationObject.hasOwnProperty(key) || validationObject[key] === this) continue; // eslint-disable-line
    await validationObject[key].validateComplete;
    if (validationObject[key].valid === false) {
      valid = false;
      break;
    }
  }
  if (valid && typeof value?.error?.code !== 'undefined') {
    return false;
  }

  return true;
};


/**
 * isValidEmail
 *
 * @param {String} email
 * @return {Boolean} True or false if the given email is valid
 */
const isValidEmail = (email) => {
  const validEmail = EMAIL_REGEX.test(email);
  if (validEmail) {
    const emailParts = email.split('@');
    if (emailParts.length === 2) {
      for (const key in CHECKABLE_DOMAINS) {
        /* istanbul ignore next */
        if (!CHECKABLE_DOMAINS.hasOwnProperty(key)) continue;
        if (emailParts[1].indexOf(key) === -1 || emailParts[0].length >= CHECKABLE_DOMAINS[key]) continue;
        return false;
      }
    }
  }

  return validEmail;
};


/**
 * Checks to see if a given value has at least one character.
 *
 * @param {Mixed} value The value being validated
 * @return {Boolean} True or false
 */
const hasAtLeastOneCharacter = (value) => /^(?=.*[A-Za-z])[\S]{5,}$/.test(value);


/**
 * Checks to see if a given value is not all digits.
 *
 * @param {Mixed} value The value being validated
 * @return {Boolean} True or false
 */
const isNotAllDigits = (value) => !/^[0-9]*$/gm.test(value);


/**
 * Checks to see if a given value is not all spaces.
 *
 * @param {Mixed} value The value being validated
 * @return {Boolean} True or false
 */
const isNotOnlySpaces = (value) => !/^\s+$/.test(value);


/**
 * Checks to see that the given email is not an email.
 *
 * @param {Mixed} value The value being validated
 * @return {Function}
 */
const isNotEmail = (value) => !isValidEmail(value);


/**
 * Checks to see that the given value is not an emoji.
 *
 * @memberof SubscriptionForm
 * @param {Mixed} value The value being validated
 * @return {Boolean}
 */
const isNotEmoji = (value) => emojiRegex().exec(value) === null;


/**
 * Checks to see if a given date is expired
 *
 * @param {String|int} value - date string|int with format YYYYMM
 * @return {Boolean}
 */
const isNotExpired = (value) => {
  if (value.length !== 6) {
    return;
  }
  const today = new Date();
  const presentYear = String(today.getFullYear());
  let presentMonth = String(today.getMonth() + 1);
  if (presentMonth < 10) {
    presentMonth = '0' + presentMonth;
  }
  const present = presentYear + presentMonth;
  return value >= present;
};


/**
 * Checks to see if a given value is not zero.
 *
 * @param {Mixed} value The value being validated
 * @return {Boolean} True or false
 */
const isNotZero = (value) => String(value) !== '0';

const isTrue = (value) => value === true;

export {
  apiInputErrorValidator,
  apiErrorValidator,
  isValidEmail,
  hasAtLeastOneCharacter,
  isNotAllDigits,
  isNotOnlySpaces,
  isNotEmail,
  isNotEmoji,
  isNotExpired,
  isNotZero,
  uncaughtApiErrorsValidator,
  isTrue
};
